import { create } from 'zustand';
import { getTimeClockGroups } from '@/api/generated/time-clock-groups/time-clock-groups';
import type {
  FacilityFlowCoreModulesPlanningWorkflowsTimeClockGroupsSaveTimeClockGroupRequest,
  FacilityFlowCoreModulesPlanningWorkflowsTimeClockGroupsTimeClockGroupDto,
} from '@/api/generated/models';
import { showApiError } from '@/errors';
import { i18n } from '@/i18n';
import { useToastStore } from '@/store/useToastStore';

export type TimeClockGroup = FacilityFlowCoreModulesPlanningWorkflowsTimeClockGroupsTimeClockGroupDto;
export type SaveTimeClockGroupRequest =
  FacilityFlowCoreModulesPlanningWorkflowsTimeClockGroupsSaveTimeClockGroupRequest;

const api = getTimeClockGroups();

const byName = (a: TimeClockGroup, b: TimeClockGroup) =>
  (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' });

interface TimeClockGroupState {
  /** All groups of the tenant, active and inactive, sorted by name. */
  groups: TimeClockGroup[];
  loaded: boolean;
  loading: boolean;
  saving: boolean;
  error: string | null;
  /** Loads the groups once; `force` reloads them (e.g. on the settings page). */
  fetchGroups: (options?: { force?: boolean }) => Promise<void>;
  /** Returns the stored group, or null when the server rejected it (toast already shown). */
  createGroup: (request: SaveTimeClockGroupRequest) => Promise<TimeClockGroup | null>;
  updateGroup: (id: string, request: SaveTimeClockGroupRequest) => Promise<TimeClockGroup | null>;
}

let inFlight: Promise<void> | null = null;

/** Time clock groups (FACF-149): seasonal clock-in windows per group of employees. */
export const useTimeClockGroupStore = create<TimeClockGroupState>((set, get) => ({
  groups: [],
  loaded: false,
  loading: false,
  saving: false,
  error: null,

  fetchGroups: async ({ force = false } = {}) => {
    if (!force && get().loaded) return;
    if (inFlight) return inFlight;
    set({ loading: true, error: null });
    inFlight = (async () => {
      try {
        const groups = await api.getApiV1TimeclockGroups();
        set({ groups: [...groups].sort(byName), loaded: true, loading: false });
      } catch (err) {
        console.error('[TimeClockGroupStore] fetch failed', err);
        set({ error: 'timeClockGroups:errors.fetchFailed', loading: false });
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  },

  createGroup: async (request) => {
    set({ saving: true });
    try {
      const created = await api.postApiV1TimeclockGroups(request);
      set((s) => ({ groups: [...s.groups, created].sort(byName), saving: false }));
      useToastStore.getState().addToast(i18n.t('timeClockGroups:messages.created'), 'success');
      return created;
    } catch (err) {
      set({ saving: false });
      showApiError(err, { skipIfFieldErrors: false });
      return null;
    }
  },

  updateGroup: async (id, request) => {
    set({ saving: true });
    try {
      const updated = await api.putApiV1TimeclockGroupsId(id, request);
      set((s) => ({
        groups: s.groups.map((g) => (g.timeClockGroupId === id ? updated : g)).sort(byName),
        saving: false,
      }));
      useToastStore.getState().addToast(i18n.t('timeClockGroups:messages.updated'), 'success');
      return updated;
    } catch (err) {
      set({ saving: false });
      showApiError(err, { skipIfFieldErrors: false });
      return null;
    }
  },
}));
